import React from 'react';

import type { TChainIndicator } from './types';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import IconSvg from 'ui/shared/IconSvg';
import NativeTokenIcon from 'ui/shared/NativeTokenIcon';

import ChainIndicatorsChart from './ChainIndicatorsChart';
import ChainIndicatorsContainer from './ChainIndicatorsContainer';
import ChainIndicatorsList from './ChainIndicatorsList';
import useChartDataQuery from './useChartDataQuery';
import { isIndicatorEnabled, sortIndicators } from './utils/indicators';

const isStatsFeatureEnabled = config.features.stats.isEnabled;
const rollupFeature = config.features.rollup;
const isOptimisticRollup = rollupFeature.isEnabled && rollupFeature.type === 'optimistic';
const isArbitrumRollup = rollupFeature.isEnabled && rollupFeature.type === 'arbitrum';

const ChainIndicators = () => {
  const statsMicroserviceQueryResult = useApiQuery('stats:pages_main', {
    queryOptions: {
      refetchOnMount: false,
      enabled: isStatsFeatureEnabled,
    },
  });

  const statsApiQueryResult = useApiQuery('general:stats', {
    queryOptions: {
      refetchOnMount: false,
    },
  });

  const apiData = statsApiQueryResult.data;
  const microserviceData = statsMicroserviceQueryResult.data;

  const indicators: Array<TChainIndicator> = React.useMemo(() => {
    return [
      {
        id: 'daily_txs' as const,
        title: (() => {
          if (isStatsFeatureEnabled && microserviceData?.daily_new_transactions?.info?.title) {
            return microserviceData.daily_new_transactions.info.title;
          }
          return 'Daily transactions';
        })(),
        value: (() => {
          const STRING_FORMAT = { maximumFractionDigits: 2, notation: 'compact' as const };
          if (isStatsFeatureEnabled) {
            if (typeof microserviceData?.yesterday_transactions?.value === 'string') {
              return Number(microserviceData.yesterday_transactions.value).toLocaleString(undefined, STRING_FORMAT);
            }
          } else {
            if (typeof apiData?.transactions_today === 'string') {
              return Number(apiData.transactions_today).toLocaleString(undefined, STRING_FORMAT);
            }
          }
          return 'N/A';
        })(),
        hint: (() => {
          if (isStatsFeatureEnabled && microserviceData?.daily_new_transactions?.info?.description) {
            return microserviceData.daily_new_transactions.info.description;
          }
          return `Number of transactions yesterday (0:00 - 23:59 UTC). The chart displays daily transactions for the past 30 days.`;
        })(),
        icon: <IconSvg name="transactions" boxSize={ 6 } bgColor="#56ACD1" borderRadius="base" color="white"/>,
      },
      {
        id: 'daily_operational_txs' as const,
        title: (() => {
          if (isStatsFeatureEnabled) {
            if (isArbitrumRollup && microserviceData?.daily_new_operational_transactions?.info?.title) {
              return microserviceData.daily_new_operational_transactions.info.title;
            }
            if (isOptimisticRollup && microserviceData?.op_stack_daily_new_operational_transactions?.info?.title) {
              return microserviceData.op_stack_daily_new_operational_transactions.info.title;
            }
          }
          return 'Daily op txns';
        })(),
        titleShort: 'Daily op txns',
        value: (() => {
          const STRING_FORMAT = { maximumFractionDigits: 2, notation: 'compact' as const };
          if (isStatsFeatureEnabled) {
            if (isArbitrumRollup && typeof microserviceData?.yesterday_operational_transactions?.value === 'string') {
              return Number(microserviceData.yesterday_operational_transactions.value).toLocaleString(undefined, STRING_FORMAT);
            }
            if (isOptimisticRollup && typeof microserviceData?.op_stack_yesterday_operational_transactions?.value === 'string') {
              return Number(microserviceData.op_stack_yesterday_operational_transactions.value).toLocaleString(undefined, STRING_FORMAT);
            }
          }
          return 'N/A';
        })(),
        hint: (() => {
          if (isStatsFeatureEnabled) {
            if (isArbitrumRollup && microserviceData?.daily_new_operational_transactions?.info?.description) {
              return microserviceData.daily_new_operational_transactions.info.description;
            }
            if (isOptimisticRollup && microserviceData?.op_stack_daily_new_operational_transactions?.info?.description) {
              return microserviceData.op_stack_daily_new_operational_transactions.info.description;
            }
          }
          return `Number of operational transactions yesterday (0:00 - 23:59 UTC). The chart displays daily operational transactions for the past 30 days.`;
        })(),
        icon: <IconSvg name="transactions" boxSize={ 6 } bgColor="#56ACD1" borderRadius="base" color="white"/>,
      },
      {
        id: 'coin_price' as const,
        title: `${ config.chain.currency.symbol } price`,
        value: typeof apiData?.coin_price !== 'string' ?
          '$N/A' :
          '$' + Number(apiData?.coin_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }),
        valueDiff: typeof apiData?.coin_price_change_percentage === 'number' ?
          apiData.coin_price_change_percentage :
          undefined,
        hint: `${ config.chain.currency.symbol } token daily price in USD.`,
        icon: <NativeTokenIcon boxSize={ 6 }/>,
      },
      {
        id: 'secondary_coin_price' as const,
        title: `${ config.chain.secondaryCoin.symbol } price`,
        value: typeof apiData?.secondary_coin_price !== 'string' ?
          '$N/A' :
          '$' + Number(apiData?.secondary_coin_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }),
        hint: `${ config.chain.secondaryCoin.symbol } token daily price in USD.`,
        icon: <NativeTokenIcon boxSize={ 6 } type="secondary"/>,
      },
      {
        id: 'market_cap' as const,
        title: 'Market cap',
        value: typeof apiData?.market_cap !== 'string' ?
          '$N/A' :
          '$' + Number(apiData.market_cap).toLocaleString(undefined, { maximumFractionDigits: 2, notation: 'compact' }),
        // eslint-disable-next-line max-len
        hint: 'The total market value of a cryptocurrency\'s circulating supply. It is analogous to the free-float capitalization in the stock market. Market Cap = Current Price x Circulating Supply.',
        icon: <IconSvg name="globe" boxSize={ 6 } bgColor="#6A5DCC" borderRadius="base" color="white"/>,
      },
      {
        id: 'tvl' as const,
        title: 'Total value locked',
        value: typeof apiData?.tvl !== 'string' ?
          '$N/A' :
          '$' + Number(apiData.tvl).toLocaleString(undefined, { maximumFractionDigits: 2, notation: 'compact' }),
        hint: 'Total value of digital assets locked or staked in a chain',
        icon: <IconSvg name="lock" boxSize={ 6 } bgColor="#517FDB" borderRadius="base" color="white"/>,
      },
    ]
      .filter(isIndicatorEnabled)
      .sort(sortIndicators);
  }, [ apiData, microserviceData ]);

  const [ selectedIndicatorId, selectIndicatorId ] = React.useState(indicators[0]?.id);
  const selectedIndicator = indicators.find(({ id }) => id === selectedIndicatorId);

  const chartQuery = useChartDataQuery(selectedIndicatorId);

  if (indicators.length === 0) {
    return null;
  }

  const isLoading = (isStatsFeatureEnabled && statsMicroserviceQueryResult.isPending) || statsApiQueryResult.isPending;

  return (
    <ChainIndicatorsContainer>
      { selectedIndicator && (
        <ChainIndicatorsChart
          isLoading={ isLoading }
          title={ selectedIndicator.title }
          hint={ selectedIndicator.hint }
          value={ selectedIndicator?.value }
          valueDiff={ selectedIndicator?.valueDiff }
          chartQuery={ chartQuery }
        />
      ) }
      <ChainIndicatorsList
        indicators={ indicators }
        isLoading={ isLoading }
        selectedId={ selectedIndicatorId }
        onItemClick={ selectIndicatorId }
      />
    </ChainIndicatorsContainer>
  );
};

export default ChainIndicators;
